import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { Measurement } from '@cosmos/shared';
import { ErrorService, RBACService } from '@cosmos/angular/utils';
import { MeasurementsService } from '@cosmos/angular/api';

interface ChannelStat {
  channelKey: string;
  count: number;
  first: Date | null;
  last: Date | null;
}

interface RemovedRow {
  channelKey: string;
  ts: Date;
  reason?: string;
  removedAt: Date;
  removedBy: string;
}

/**
 * Read + act on a measurement's editing trace: the immutable raw buffer stats,
 * the active crop window (with inline clear), and the removed-datapoints list
 * (with per-row restore). Opened via DialogService.
 *
 * config.data: { measurement: Measurement; onChange: () => void }
 */
@Component({
  selector: 'cosmos-data-trace-dialog',
  imports: [DatePipe, TranslatePipe, ButtonModule, TableModule, TagModule, DividerModule, MessageModule],
  templateUrl: './data-trace-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTraceDialogComponent {
  private readonly config = inject(DynamicDialogConfig);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly errorService = inject(ErrorService);
  private readonly translate = inject(TranslateService);
  protected readonly rbac = inject(RBACService);

  private readonly onChange: () => void = this.config.data?.onChange ?? (() => undefined);

  readonly measurement = signal<Measurement>(this.config.data.measurement);
  readonly clearingWindow = signal(false);

  readonly window = computed(() => this.measurement().window ?? null);

  readonly channelStats = computed<ChannelStat[]>(() => {
    const all = (this.measurement() as any).channelDataAll ?? {};
    return Object.keys(all).map((channelKey) => {
      const times: Date[] = (all[channelKey]?.time ?? []).map((t: any) => new Date(t));
      return {
        channelKey,
        count: times.length,
        first: times.length ? times[0] : null,
        last: times.length ? times[times.length - 1] : null,
      };
    });
  });

  readonly removedRows = computed<RemovedRow[]>(() =>
    (this.measurement().removedPoints ?? []).map((rp) => ({
      channelKey: rp.channelKey ?? '',
      ts: new Date(rp.ts),
      reason: rp.reason,
      removedAt: new Date(rp.removedAt),
      removedBy: rp.removedBy,
    })),
  );

  private readonly restoringKey = signal<string | null>(null);
  isRestoring(row: RemovedRow): boolean {
    return this.restoringKey() === row.channelKey + '|' + row.ts.getTime();
  }

  clearWindow(): void {
    this.clearingWindow.set(true);
    this.measurementsService
      .rescheduleWindow(this.measurement()._id!, { startTs: null, endTs: null })
      .pipe(finalize(() => this.clearingWindow.set(false)))
      .subscribe({
        next: (res) => { if (res.body) this.measurement.set(res.body); this.onChange(); },
        error: (e) => this.errorService.handleError(e),
      });
  }

  restore(row: RemovedRow): void {
    this.restoringKey.set(row.channelKey + '|' + row.ts.getTime());
    this.measurementsService
      .restoreDatapoint(this.measurement()._id!, { channelKey: row.channelKey, ts: row.ts.toISOString() })
      .pipe(finalize(() => this.restoringKey.set(null)))
      .subscribe({
        next: (res) => {
          if (res.body) this.measurement.set(res.body);
          this.errorService.notify('success', this.translate.instant('measurement.dataTrace.restoreSuccess'));
          this.onChange();
        },
        error: (e) => this.errorService.handleError(e),
      });
  }
}
